import React, { useState } from "react"
import classNames from "classnames"
import { Upload, Button, message } from "antd"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { useTranslation } from "react-i18next"
import styles from "./MediaUpload.module.css"
import { ACCEPTED_MEDIA_TYPES, MAX_MEDIA_SIZE } from "constants/form"
import { colors } from "themes/variables"
import { processMediaUpload } from "utils/media-upload"
import PropTypes from "prop-types"
import ImageCropper from "ui/ImageCropper"

export const MediaUpload = ({
  className = "",
  onFileListChange,
  acceptedTypes = [...ACCEPTED_MEDIA_TYPES.image, ...ACCEPTED_MEDIA_TYPES.video],
  imageInfo = "",
  videoInfo = "",
  invalidFormatMessage,
  sizeLimitExceededMessage,
  maxImageSize = MAX_MEDIA_SIZE.image,
  maxVideoSize = MAX_MEDIA_SIZE.video,
  lastPosition,
  maxCount,
  maxCountErrorMessage,
  initialCount = 0,
  requireImageCrop = true,
  cropAspect = 1,
  lockCropSize = true,
  showUploadSuccessMessage = true,
  cropMinNaturalWidth,
  cropMinNaturalHeight
}) => {
  const { t } = useTranslation()
  const [fileList, setFileList] = useState([])
  const [cropVisible, setCropVisible] = useState(false)
  const [cropSrc, setCropSrc] = useState(null)
  const [pendingFile, setPendingFile] = useState(null)
  let uploadedFilesCount = initialCount
  const handleUpload = async (file) => {
    try {
      setFileList((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: "uploading", percent: 0 } : f)))
      const { photo, video, attributes } = await processMediaUpload(file)

      if (maxCount && uploadedFilesCount >= maxCount) {
        throw new Error(maxCountErrorMessage)
      }

      const media = photo || video
      media.position = lastPosition
      attributes.position = lastPosition
      onFileListChange([media], [attributes])
      setFileList((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: "done", percent: 100 } : f)))
      if (showUploadSuccessMessage) message.success(`${file.name} uploaded successfully!`)
      uploadedFilesCount++
    } catch (error) {
      setFileList((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: "error" } : f)))
      if (error instanceof Error && error.message) {
        message.error(error.message)
      } else {
        message.error(`${file.name} upload failed.`)
      }
    }
  }

  const openCropper = (file) => {
    try {
      const src = URL.createObjectURL(file)
      setCropSrc(src)
      setPendingFile(file)
      setCropVisible(true)
    } catch (e) {
      message.error(t("media_upload.messages.invalid_format"))
      console.error(e)
    }
  }

  const closeCropper = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    setPendingFile(null)
    setCropVisible(false)
  }

  const handleImageCropComplete = (croppedBlob, cropInfo) => {
    if (!croppedBlob || !pendingFile) {
      closeCropper()
      return
    }

    const originalFile = pendingFile
    const base = (originalFile.name || "image").replace(/\.[^/.]+$/, "")
    const croppedFile = new File([croppedBlob], `${base}-cropped.jpg`, { type: "image/jpeg" })
    closeCropper()

    if (maxCount && uploadedFilesCount >= maxCount) {
      message.error(maxCountErrorMessage)
      return
    }

    const numericUid = Date.now() + Math.floor(Math.random() * 1000)
    const filePreview = URL.createObjectURL(croppedBlob)

    const photo = {
      uid: `local-${originalFile.uid}`,
      file: croppedFile,
      originalFile,
      file_type: "photo",
      file_preview: filePreview,
      mediaType: originalFile.type.split("/")[0],
      id: numericUid,
      ...(cropInfo && { cropInfo })
    }

    const attributes = {
      file: croppedFile,
      originalFile,
      file_type: "photo",
      ...(cropInfo && { cropInfo })
    }

    photo.position = lastPosition
    attributes.position = lastPosition
    onFileListChange([photo], [attributes])
    uploadedFilesCount++
    if (showUploadSuccessMessage) message.success(`${originalFile.name} uploaded successfully!`)
  }

  const beforeUpload = (file) => {
    const isValidType = acceptedTypes.includes(file.type)
    if (!isValidType) {
      message.error(invalidFormatMessage || t("media_upload.messages.invalid_format"))
      return Upload.LIST_IGNORE
    }

    const isImage = file.type.startsWith("image/")
    const isVideo = file.type.startsWith("video/")

    if (isImage && file.size / 1024 / 1024 > maxImageSize) {
      message.error(
        sizeLimitExceededMessage ||
          t("media_upload.messages.image_size_limit_exceeded", {
            maxSize: maxImageSize
          })
      )
      return Upload.LIST_IGNORE
    }

    if (isVideo && file.size / 1024 / 1024 > maxVideoSize) {
      message.error(
        sizeLimitExceededMessage ||
          t("media_upload.messages.video_size_limit_exceeded", {
            maxSize: maxVideoSize
          })
      )
      return Upload.LIST_IGNORE
    }

    // If image crop is required, open cropper and stop default flow
    if (isImage && requireImageCrop) {
      openCropper(file)
      return Upload.LIST_IGNORE
    }

    // Default flow (videos or when cropping is not required)
    setFileList((prev) => [
      ...prev,
      {
        uid: file.uid,
        name: file.name,
        status: "uploading",
        percent: 0,
        originFileObj: file
      }
    ])

    handleUpload(file)
    return false
  }

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList)
  }

  return (
    <>
      <Upload
        accept={acceptedTypes.join(",")}
        customRequest={() => {}}
        onChange={onChange}
        beforeUpload={beforeUpload}
        fileList={fileList}
        showUploadList={false}
        className="block"
      >
        <Button className={classNames(className, styles.upload)} variant="dashed">
          <div className="align-center justify-center">
            <div className="flex-column justify-center align-center">
              <div className={styles.circle}>
                <Icon name="Upload" width={16} height={16} strokeWidth={2.4} />
              </div>
              <Text variant="link" className="mb-xxs">
                {t("global.upload")}
              </Text>
              {imageInfo && (
                <Text className="flex-1" variant="caption" size="md" color={colors.textSecondary}>
                  {imageInfo}
                </Text>
              )}
              {videoInfo && (
                <Text className="flex-1" variant="caption" size="md" color={colors.textSecondary}>
                  {videoInfo}
                </Text>
              )}
            </div>
          </div>
        </Button>
      </Upload>
      {requireImageCrop && cropVisible && (
        <ImageCropper
          visible={cropVisible}
          onClose={closeCropper}
          imageSrc={cropSrc || ""}
          onCropComplete={handleImageCropComplete}
          aspect={cropAspect}
          lockSize={lockCropSize}
          minNaturalWidth={cropMinNaturalWidth}
          minNaturalHeight={cropMinNaturalHeight}
        />
      )}
    </>
  )
}

MediaUpload.propTypes = {
  className: PropTypes.string,
  onFileListChange: PropTypes.func,
  acceptedTypes: PropTypes.arrayOf(PropTypes.string),
  imageInfo: PropTypes.string,
  videoInfo: PropTypes.string,
  invalidFormatMessage: PropTypes.string,
  sizeLimitExceededMessage: PropTypes.string,
  maxImageSize: PropTypes.number,
  maxVideoSize: PropTypes.number,
  lastPosition: PropTypes.number,
  maxCount: PropTypes.number,
  maxCountErrorMessage: PropTypes.string,
  initialCount: PropTypes.number,
  requireImageCrop: PropTypes.bool,
  cropAspect: PropTypes.number,
  lockCropSize: PropTypes.bool,
  showUploadSuccessMessage: PropTypes.bool,
  cropMinNaturalWidth: PropTypes.number,
  cropMinNaturalHeight: PropTypes.number
}
